import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import HubLightBackground from './HubLightBackground';

// Light-mode page background for every page that has the global header (Home draws its own copy inline).
//
// It puts the same soft white background with contour waves as the Home page (HubLightBackground) on every
// page that used to have a white / plain background. Two parts:
//
//  1. The waves are drawn on a fixed layer that is portalled straight into <body> and sits BEHIND everything
//     (z-index -1), so it can never cover page content, whatever the page does with stacking.
//  2. A page's plain white / light-grey wrapper would hide that layer, so the wrappers around the header
//     (the page's root containers, plus <body> / <html>) are checked by their real, computed background:
//     if it is a plain, opaque, near-white and un-tinted colour with no image or gradient, the element is
//     tagged and made transparent so the waves show through. Pages with a dark, coloured, tinted, gradient
//     or image / map background are recognised as not plain and are left exactly as they were. Cards,
//     tables and panels inside the page keep their own white backgrounds, as before.

export const PLAIN_BG_ATTR = 'data-c30-plain-bg';

// Reads "rgb(r, g, b)" / "rgba(r, g, b, a)" / "rgb(r g b / a)" as returned by getComputedStyle.
export const parseColor = (value) => {
  const m = /^rgba?\(([^)]+)\)$/.exec(String(value || '').trim());
  if (!m) return null;
  const parts = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
  if (parts.length < 3 || parts.slice(0, 3).some((n) => Number.isNaN(n))) return null;
  const a = parts.length > 3 ? parts[3] : 1;
  if (Number.isNaN(a)) return null;
  return { r: parts[0], g: parts[1], b: parts[2], a };
};

// "Plain" = opaque enough to hide the waves, very light (white, gray-50, gray-100, #f5f5f5 ...) and not
// tinted (so orange-50 / blue-50 style tints, dark themes, and any coloured page are NOT plain).
export const isPlainLightBackground = (el) => {
  const cs = window.getComputedStyle(el);
  if (cs.backgroundImage && cs.backgroundImage !== 'none') return false;
  const c = parseColor(cs.backgroundColor);
  if (!c || c.a < 0.6) return false;
  const max = Math.max(c.r, c.g, c.b);
  const min = Math.min(c.r, c.g, c.b);
  const luminance = (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
  const saturation = max === 0 ? 0 : (max - min) / max;
  return luminance >= 0.92 && saturation <= 0.035;
};

// Re-checks every element in the chain against its ORIGINAL background: the tag is removed first (so the
// computed colour is the page's own, not our transparent override), then set again if it is plain. All of it
// happens in one synchronous pass, so nothing is painted in between and there is no flicker.
export const applyPlainBackgrounds = (chain) => {
  chain.forEach((el) => {
    el.removeAttribute(PLAIN_BG_ATTR);
    if (isPlainLightBackground(el)) el.setAttribute(PLAIN_BG_ATTR, '1');
  });
};

export const useLightPageBackground = (startRef, active, routeKey) => {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined;
    const start = startRef.current;
    if (!start) return undefined;
    const chain = [];
    for (let el = start; el; el = el.parentElement) chain.push(el);
    const apply = () => applyPlainBackgrounds(chain);
    apply();
    // Late styles (fonts, lazy CSS) and pages that swap their wrapper's classes / inline style after mount.
    const settle = setTimeout(apply, 400);
    const observer = new MutationObserver(apply);
    chain.forEach((el) => observer.observe(el, { attributes: true, attributeFilter: ['class', 'style'] }));
    return () => {
      clearTimeout(settle);
      observer.disconnect();
      chain.forEach((el) => el.removeAttribute(PLAIN_BG_ATTR));
    };
  }, [startRef, active, routeKey]);
};

const LightPageBackground = ({ startRef, active, routeKey }) => {
  useLightPageBackground(startRef, active, routeKey);
  if (!active || typeof document === 'undefined') return null;
  return (
    <>
      <style>{`[${PLAIN_BG_ATTR}] { background: transparent !important; }`}</style>
      {createPortal(
        <div
          style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, zIndex: -1, pointerEvents: 'none' }}
          data-testid="page-light-background"
        >
          <HubLightBackground />
        </div>,
        document.body,
      )}
    </>
  );
};

export default LightPageBackground;
